import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router';

import { ArtificialIntelligenceApi } from 'Api/ArtificialIntelligence/ArtificialIntelligenceApi';
import { ControlsApi } from 'Api/Controls/ControlsApi';
import { DocumentApi } from 'Api/Document/DocumentApi';
import { IssuesApi } from 'Api/Issues/IssuesApi';
import { ClientTPRMApi } from 'Api/TPRM/TPRMApi';
import { ClientUcrmApi } from 'Api/UCRM/UcrmApi';
import { getOrganizationDetails, getOrganizations } from 'Api/UcrmTprm/UcrmTprmApi';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { Text } from 'Components/Text/Text';
import { TPRM, UCRM } from 'Config/Paths';
import { BreadcrumbSegment, getManageIssueOriginBreadcrumbSegments, parseManageIssueOrigin } from 'Helpers/URLBuilder/URLBuilder';
import { useControlMappingItems } from 'Hooks/ControlMapping';
import { OrganizationResponseWithAssessmentSubjectsBase } from 'Models/AssessmentSubject';
import { IssueResponse } from 'Models/Issues';
import { ActiveState, OrganizationResponseBase } from 'Models/Organization';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';

import { ManageIssueForm, ManageIssueFormProps } from '../Components/ManageIssueForm/ManageIssueForm';

export interface ManageIssueProps {
    artificialIntelligenceApi: ArtificialIntelligenceApi;
    issuesApi: IssuesApi;
    controlsApi: ControlsApi;
    documentApi: DocumentApi;
    moduleApi: ClientTPRMApi | ClientUcrmApi;
}

/**
 * Ultimately renders a page that can be used to create, update, or delete an issue in the Operational Controls module or in the TPRM module.
 * This component is responsible for examining the URL to determine the type of issue being managed. It determines which single child needs to be rendered; the child is responsible for fetching data specific to the user's desired action and for passing that data into `ManageIssueForm`.
 *
 * This structure/flow is used for two main reasons:
 * 1. To avoid fetching data that is not needed for the user's desired action. (For example, if the user is managing a TPRM issue, we do not want to use `useControlMappingItems`.)
 * 2. To improve readability. Without these intermediary components, it becomes very difficult to understand which data is supposed to be fetched and when it will be available.
 */
export const ManageIssue = (props: ManageIssueProps) => {
    const { issueId } = useParams();
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const impactedEntityId = query.get('impactedEntityId') ?? undefined;
    const issueType = location.pathname.startsWith(`/${TPRM}`) ? ProductModule.TPRM : location.pathname.startsWith(`/${UCRM}`) ? ProductModule.UCRM : ProductModule.CONTROLS;
    const isClosingIssue = query.get('closing') === String(true);

    const origin = parseManageIssueOrigin(query);
    const originBreadcrumbSegments = getManageIssueOriginBreadcrumbSegments(origin);

    switch (issueType) {
        case ProductModule.CONTROLS:
            if (issueId) {
                return <EditControlIssue {...props} issueId={issueId} isClosingIssue={isClosingIssue} originBreadcrumbSegments={originBreadcrumbSegments} />;
            } else {
                return <CreateControlIssue {...props} preselectedControlId={impactedEntityId} originBreadcrumbSegments={originBreadcrumbSegments} />;
            }
        case ProductModule.TPRM:
        case ProductModule.UCRM:
            if (issueId) {
                return <EditOrganizationIssue {...props} issueId={issueId} isClosingIssue={isClosingIssue} originBreadcrumbSegments={originBreadcrumbSegments} />;
            } else {
                return <CreateOrganizationIssue {...props} module={issueType} preselectedOrganizationId={impactedEntityId} originBreadcrumbSegments={originBreadcrumbSegments} />;
            }
    }
};

/**
 * Renders `ManageIssueForm` for the use case of creating an Operational Controls issue, after fetching all controls to which the issue can be mapped.
 */
const CreateControlIssue = (props: ManageIssueProps & { preselectedControlId?: string; originBreadcrumbSegments: BreadcrumbSegment[] }) => {
    const [controlMappingItems, controlMappingItemsError] = useControlMappingItems(props.controlsApi);

    if (controlMappingItemsError) {
        return <Text>{controlMappingItemsError.message}</Text>;
    } else if (controlMappingItems === undefined) {
        return <Placeholder />;
    } else {
        const manageIssueFormProps: ManageIssueFormProps = {
            type: 'creatingControlIssue',
            artificialIntelligenceApi: props.artificialIntelligenceApi,
            issuesApi: props.issuesApi,
            documentApi: props.documentApi,
            originBreadcrumbSegments: props.originBreadcrumbSegments,
            controls: controlMappingItems,
            preselectedControlId: props.preselectedControlId,
        };
        return <ManageIssueForm {...manageIssueFormProps} />;
    }
};

/**
 * Renders `ManageIssueForm` for the use case of managing an Operational Controls issue, after fetching details for the issue and all controls to which the issue can be mapped.
 */
const EditControlIssue = (props: ManageIssueProps & { issueId: string; isClosingIssue: boolean; originBreadcrumbSegments: BreadcrumbSegment[] }) => {
    const [controlMappingItems, controlMappingItemsError] = useControlMappingItems(props.controlsApi);
    const [issue, setIssue] = useState<IssueResponse>();
    const [requestError, setRequestError] = useState<Error>();

    useEffect(() => {
        const getIssue = async (): Promise<void> => {
            try {
                const response = await props.issuesApi.getIssue(props.issueId);
                setIssue(response.data as IssueResponse);
            } catch (error) {
                setRequestError(error);
            }
        };

        getIssue();
    }, [props.issuesApi, props.issueId]);

    if (controlMappingItemsError) {
        return <Text>{controlMappingItemsError.message}</Text>;
    } else if (requestError) {
        return <Text>{requestError.message}</Text>;
    } else if (controlMappingItems === undefined || issue === undefined) {
        return <Placeholder />;
    } else {
        const manageIssueFormProps: ManageIssueFormProps = {
            type: 'editingControlIssue',
            artificialIntelligenceApi: props.artificialIntelligenceApi,
            issuesApi: props.issuesApi,
            documentApi: props.documentApi,
            controls: controlMappingItems,
            issue: issue,
            isClosingIssue: props.isClosingIssue,
            originBreadcrumbSegments: props.originBreadcrumbSegments,
        };
        return <ManageIssueForm {...manageIssueFormProps} />;
    }
};

/**
 * Renders `ManageIssueForm` for the use case of creating a TPRM or UCRM issue, after fetching all of the module's active organizations (third parties or businesses) to which the issue can be mapped.
 */
const CreateOrganizationIssue = (props: ManageIssueProps & { module: DueDiligenceModule; preselectedOrganizationId?: string; originBreadcrumbSegments: BreadcrumbSegment[] }) => {
    const [organizations, setOrganizations] = useState<OrganizationResponseWithAssessmentSubjectsBase[]>();
    const [requestError, setRequestError] = useState<Error>();

    useEffect(() => {
        const fetchOrganizations = async (): Promise<void> => {
            try {
                const response = await getOrganizations(props.moduleApi);
                setOrganizations(response.data.filter((organization) => organization.active_state === ActiveState.ACTIVE));
            } catch (error) {
                setRequestError(error);
            }
        };

        fetchOrganizations();
    }, [props.moduleApi]);

    if (requestError) {
        return <Text>{requestError.message}</Text>;
    } else if (organizations === undefined) {
        return <Placeholder />;
    } else {
        const manageIssueFormProps: ManageIssueFormProps = {
            type: 'creatingOrganizationIssue',
            module: props.module,
            artificialIntelligenceApi: props.artificialIntelligenceApi,
            issuesApi: props.issuesApi,
            documentApi: props.documentApi,
            originBreadcrumbSegments: props.originBreadcrumbSegments,
            organizations: organizations,
            preselectedOrganizationId: props.preselectedOrganizationId,
        };
        return <ManageIssueForm {...manageIssueFormProps} />;
    }
};

/**
 * Renders `ManageIssueForm` for the use case of managing a TPRM or UCRM issue, after fetching details for the issue and its impacted organization.
 */
const EditOrganizationIssue = (props: ManageIssueProps & { issueId: string; isClosingIssue: boolean; originBreadcrumbSegments: BreadcrumbSegment[] }) => {
    const [issue, setIssue] = useState<IssueResponse>();
    const [organization, setOrganization] = useState<OrganizationResponseBase>();
    const [requestError, setRequestError] = useState<Error>();

    useEffect(() => {
        const getIssue = async (): Promise<void> => {
            try {
                const response = await props.issuesApi.getIssue(props.issueId);
                setIssue(response.data as IssueResponse);
            } catch (error) {
                setRequestError(error);
            }
        };

        getIssue();
    }, [props.issuesApi, props.issueId]);

    useEffect(() => {
        const fetchOrganization = async (organizationId: string): Promise<void> => {
            try {
                const response = await getOrganizationDetails(props.moduleApi, organizationId);
                setOrganization(response.data);
            } catch (error) {
                setRequestError(error);
            }
        };

        if (issue) {
            fetchOrganization(issue.impacted_vendor!);
        }
    }, [props.moduleApi, issue]);

    if (requestError) {
        return <Text>{requestError.message}</Text>;
    } else if (organization === undefined || issue === undefined) {
        return <Placeholder />;
    } else {
        const manageIssueFormProps: ManageIssueFormProps = {
            type: 'editingOrganizationIssue',
            artificialIntelligenceApi: props.artificialIntelligenceApi,
            issuesApi: props.issuesApi,
            documentApi: props.documentApi,
            issue: issue,
            organization: organization,
            isClosingIssue: props.isClosingIssue,
            originBreadcrumbSegments: props.originBreadcrumbSegments,
        };
        return <ManageIssueForm {...manageIssueFormProps} />;
    }
};
